"use client";

import { useState, useEffect } from "react";
import Sidebar from "./Sidebar";
import DashboardHeader from "./Header";
import ToastProvider from "../ui/ToastProvider";
import { ConnectRequestProvider } from "../../contexts/ConnectRequestContext";
import ConnectRequestModal from "../ConnectRequestModal";
import ConnectRequestListener from "../ConnectRequestListener";
import AdminCUDListener from "../AdminCUDListener";
import styles from "./Layout.module.css";

interface DashboardLayoutProps {
  children: React.ReactNode;
  title?: string;
}

export default function DashboardLayout({ children, title }: DashboardLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsTablet(width > 768 && width <= 1024);
      
      // Auto-collapse sidebar on tablet
      if (width > 768 && width <= 1024) {
        setSidebarCollapsed(true);
      } else if (width > 1024) {
        setSidebarCollapsed(false);
      }
      
      // Close mobile sidebar when resizing to desktop
      if (width > 768) {
        setSidebarOpen(false);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const handleSidebarToggle = () => {
    if (isMobile) {
      setSidebarOpen(!sidebarOpen);
    } else {
      setSidebarCollapsed(!sidebarCollapsed);
    }
  };

  const handleOverlayClick = () => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  };
  
  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    if (isMobile && sidebarOpen) {
      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        const sidebar = target.closest('[class*="sidebar"]');
        const menuToggle = target.closest('button[aria-label*="menu" i], button[aria-label*="Mở"]');
        if (!sidebar && !menuToggle) {
          setSidebarOpen(false);
        }
      };
      
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMobile, sidebarOpen]);

  return (
    <ConnectRequestProvider>
      <div className={styles.dashboardLayout}>
        <Sidebar 
          isCollapsed={isMobile ? false : sidebarCollapsed}
          onToggle={handleSidebarToggle}
          onCollapseChange={(c: boolean) => {
            if (!isMobile) {
              setSidebarCollapsed(c);
            }
          }}
          isOpen={isMobile ? sidebarOpen : undefined}
        />
        
        {isMobile && sidebarOpen && (
          <div className={styles.sidebarOverlay} onClick={handleOverlayClick} />
        )}

        <div className={`${styles.dashboardMain} ${!isMobile && sidebarCollapsed ? styles.sidebarCollapsed : ''} ${isMobile && sidebarOpen ? styles.sidebarOpen : ''}`}>
          <DashboardHeader 
            title={title}
            onMenuToggle={handleSidebarToggle}
            showMenuToggle={isMobile || isTablet}
          />
          
          <main className={styles.dashboardContent}>
            <div className={styles.dashboardContentInner}>
              {children}
            </div>
          </main>
        </div>
        
        <ToastProvider />
        <ConnectRequestListener />
        <AdminCUDListener />
        <ConnectRequestModal />
      </div>
    </ConnectRequestProvider>
  );
}

